import { forwardRef, useEffect, useRef, useState } from 'react'
import NumberFormat from 'react-number-format'

// A live-formatted currency input (thousands separators, always .00) for an
// Amount field -- MUI TextField's InputProps.inputComponent slot. NOT the
// same component as inputs.jsx's NumberFormatInput, deliberately: that one's
// `fixedDecimalScale` combined with `allowEmptyFormatting` is unusable for
// live typing (verified via an isolated jest repro) --
//   - fixedDecimalScale forces every keystroke through the fixed decimal
//     places instead of the whole-number part, e.g. typing "100" from empty
//     becomes "1.00" -- so it can't just be turned on permanently.
//   - allowEmptyFormatting pre-renders the empty field as ".00" before the
//     user ever types anything, which then corrupts the very first
//     keystrokes into that pre-filled mask (e.g. "0100" instead of "100")
//     even once fixedDecimalScale is toggled off for the typing itself.
// The fix here: keep the field genuinely empty until the user has typed
// something (no allowEmptyFormatting), and only force fixedDecimalScale
// while the field is NOT focused -- typing sees plain thousands-separated
// digits. react-number-format's own onFocus doesn't work reliably for
// detecting focus -- its onFocus is internally deferred via setTimeout(0) (a
// Chrome/Safari caret-position workaround), which can still be mid-flight
// when the first keystroke lands. Listening to the underlying input's native
// focus event directly (native events are synchronous) sidesteps that
// entirely for knowing WHEN focus changed.
//
// react-number-format v4 also keeps its OWN internal formatted-string state,
// decoupled from props across renders -- changing fixedDecimalScale alone on
// a later render does NOT retroactively reformat a value it already
// displayed; it only changes how the NEXT keystroke gets formatted. Force it
// on blur by imperatively writing the correctly-formatted string straight
// onto the DOM node, deferred via setTimeout(0) (after the blur-triggered
// re-render commits, matching react-number-format's own setTimeout(0) usage
// noted above) rather than synchronously -- bypasses react-number-format's
// controlled-value machinery entirely, which is safe here since that
// re-render (`focused` flipping, with the numeric `value` prop itself
// unchanged) is one react-number-format doesn't consider significant enough
// to touch the DOM value on its own, so there's nothing to fight with.
//
// Losing focus goes through NumberFormat's own onBlur prop, NOT a native
// 'blur' listener. NumberFormat clears its internal `focusedElm` in its own
// blur handler, and only THEN calls the onBlur prop. A native listener runs
// first (before React's synthetic blur handling), so flipping `focused` there
// re-rendered NumberFormat while it still believed the field was focused: its
// componentDidUpdate reformatted the value (fixedDecimalScale just changed)
// and, with a focused element on record, restored the caret by calling
// el.focus() -- pulling focus back to this field from whichever field the
// user had just clicked, so their next keystrokes landed here instead.
//
// Do NOT "fix" this by remounting NumberFormat (e.g. a `key` tied to
// `focused`) to force a full reformat instead -- remounting destroys the
// actual DOM <input> and creates a new one, and the new one does not inherit
// browser focus, silently breaking the ability to type into the field at all
// the moment it's first focused. Never remount a focused form control.
//
// Shared by the admin (investment-transfers, investment-value-
// adjustments, balance-adjustments) and donor (investment transfer dialog)
// amount fields -- lives with the shared form fields, not one feature.
export const CurrencyInput = forwardRef((props, ref) => {
  const { onChange, onBlur, ...other } = props
  const [focused, setFocused] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    const el = inputRef.current
    if (!el) return undefined
    const handleFocus = () => setFocused(true)
    el.addEventListener('focus', handleFocus)
    return () => el.removeEventListener('focus', handleFocus)
  }, [])

  useEffect(() => {
    if (focused) return undefined
    const timeoutId = setTimeout(() => {
      const el = inputRef.current
      if (!el || el.value === '') return
      const parsed = parseFloat(el.value.replace(/,/g, ''))
      if (!Number.isNaN(parsed)) {
        el.value = parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      }
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [focused])

  return (
    <NumberFormat
      {...other}
      onBlur={(e) => {
        setFocused(false)
        if (onBlur) onBlur(e)
      }}
      getInputRef={(node) => {
        inputRef.current = node
        const forwarded = ref
        if (typeof forwarded === 'function') forwarded(node)
        else if (forwarded) forwarded.current = node
      }}
      isNumericString={false}
      onValueChange={(values) => {
        // floatValue is undefined for an empty field, not 0 -- `|| 0` used to
        // collapse both to the number 0, so clearing a typed value looked
        // identical to the admin having typed "0". A consumer's required-
        // field check (=== '') never caught it once any digit had been
        // typed, since it always saw a number after that point. Preserve
        // the distinction: emit '' when empty, the real number otherwise.
        onChange({
          target: {
            name: props.name,
            value: values.floatValue === undefined ? '' : values.floatValue,
          },
        })
      }}
      allowLeadingZeros={false}
      thousandSeparator
      displayType="input"
      decimalScale={2}
      fixedDecimalScale={!focused}
    />
  )
})
